import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Package, 
  Boxes, 
  AlertTriangle, 
  TrendingDown,
  ArrowDownToLine,
  ArrowUpFromLine,
  RefreshCw,
  Loader2
} from 'lucide-react';
import { dashboardApi } from '@/api/client';
import type { DashboardData } from '@/types';
import { format, parseISO } from 'date-fns';
import { ja } from 'date-fns/locale';
import clsx from 'clsx';

export function DashboardPage() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboard = async () => {
    setIsLoading(true);
    const response = await dashboardApi.get();
    if (response.success && response.data) {
      setData(response.data);
      setError(null);
    } else {
      setError(response.error || 'データの取得に失敗しました');
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-700 p-4 rounded-lg">
        {error}
        <button onClick={fetchDashboard} className="ml-4 underline">
          再読み込み
        </button>
      </div>
    );
  }

  if (!data) return null;

  const stats = [
    {
      name: '登録商品数',
      value: data.stats.total_products,
      icon: Package,
      color: 'bg-blue-500',
      link: '/products',
    },
    {
      name: '在庫ロット数',
      value: data.stats.total_lots,
      icon: Boxes,
      color: 'bg-green-500',
      link: '/inventory',
    },
    {
      name: '期限切れ間近',
      value: data.stats.expiring_soon,
      icon: AlertTriangle,
      color: data.stats.expiring_soon > 0 ? 'bg-yellow-500' : 'bg-gray-400',
      link: '/alerts',
      alert: data.stats.expiring_soon > 0,
    },
    {
      name: '在庫不足',
      value: data.stats.low_stock,
      icon: TrendingDown,
      color: data.stats.low_stock > 0 ? 'bg-red-500' : 'bg-gray-400',
      link: '/alerts',
      alert: data.stats.low_stock > 0,
    },
  ];

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'in': return '入庫';
      case 'out': return '出庫';
      case 'adjust': return '調整';
      default: return type;
    }
  };

  const getTypeBadgeClass = (type: string) => {
    switch (type) {
      case 'in': return 'badge-success';
      case 'out': return 'badge-info';
      case 'adjust': return 'badge-warning';
      default: return 'badge-info';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">ダッシュボード</h1>
        <button
          onClick={fetchDashboard}
          className="btn-ghost flex items-center gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          更新
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat) => (
          <Link
            key={stat.name}
            to={stat.link}
            className={clsx(
              'card p-4 hover:shadow-md transition-shadow',
              stat.alert && 'ring-2 ring-yellow-400'
            )}
          >
            <div className="flex items-center gap-3">
              <div className={clsx('p-2 rounded-lg', stat.color)}>
                <stat.icon className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{stat.name}</p>
                <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {/* Today's Activity */}
      <div className="card">
        <div className="card-header">
          <h2 className="font-semibold text-gray-900">本日の入出庫</h2>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="p-3 bg-green-50 rounded-lg">
              <div className="flex items-center justify-center gap-2 text-green-700">
                <ArrowDownToLine className="h-4 w-4" />
                <span className="text-sm">入庫</span>
              </div>
              <p className="text-2xl font-bold text-green-700 mt-1">
                {data.today.in_count}
              </p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center justify-center gap-2 text-blue-700">
                <ArrowUpFromLine className="h-4 w-4" />
                <span className="text-sm">出庫</span>
              </div>
              <p className="text-2xl font-bold text-blue-700 mt-1">
                {data.today.out_count}
              </p>
            </div>
            <div className="p-3 bg-yellow-50 rounded-lg">
              <div className="flex items-center justify-center gap-2 text-yellow-700">
                <RefreshCw className="h-4 w-4" />
                <span className="text-sm">調整</span>
              </div>
              <p className="text-2xl font-bold text-yellow-700 mt-1">
                {data.today.adjust_count}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Expiring Items */}
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">期限切れ間近</h2>
            <Link to="/alerts" className="text-sm text-primary-600 hover:underline">
              すべて見る
            </Link>
          </div>
          <div className="card-body">
            {data.expiring_items.length === 0 ? (
              <p className="text-gray-500 text-sm py-4 text-center">
                期限切れ間近の在庫はありません
              </p>
            ) : (
              <ul className="divide-y">
                {data.expiring_items.slice(0, 5).map((item) => {
                  const daysUntilExpiry = item.days_until_expiry ?? null;
                  return (
                    <li key={item.id} className="py-3 flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{item.product_name}</p>
                        <p className="text-sm text-gray-500">
                          ロット: {item.lot_number} / {item.quantity}{item.product_unit}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className={clsx(
                          'badge',
                          daysUntilExpiry !== null && daysUntilExpiry <= 0
                            ? 'badge-danger'
                            : daysUntilExpiry !== null && daysUntilExpiry <= 3
                            ? 'badge-warning'
                            : 'badge-info'
                        )}>
                          {daysUntilExpiry !== null && daysUntilExpiry <= 0
                            ? '期限切れ'
                            : `あと${daysUntilExpiry}日`}
                        </span>
                        {item.expiry_date && (
                          <p className="text-xs text-gray-400 mt-1">
                            {format(parseISO(item.expiry_date), 'M/d', { locale: ja })}
                          </p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h2 className="font-semibold text-gray-900">最近の入出庫</h2>
            <Link to="/history" className="text-sm text-primary-600 hover:underline">
              すべて見る
            </Link>
          </div>
          <div className="card-body">
            {data.recent_transactions.length === 0 ? (
              <p className="text-gray-500 text-sm py-4 text-center">
                まだ入出庫履歴がありません
              </p>
            ) : (
              <ul className="divide-y">
                {data.recent_transactions.map((tx) => (
                  <li key={tx.id} className="py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className={getTypeBadgeClass(tx.type)}>
                        {getTypeLabel(tx.type)}
                      </span>
                      <div>
                        <p className="font-medium text-gray-900">{tx.product_name}</p>
                        <p className="text-sm text-gray-500">{tx.lot_number}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={clsx(
                        'font-medium',
                        tx.type === 'in' ? 'text-green-600' : 
                        tx.type === 'out' ? 'text-blue-600' : 'text-yellow-600'
                      )}>
                        {tx.type === 'in' ? '+' : tx.type === 'out' ? '-' : ''}
                        {Math.abs(tx.quantity)}
                      </p>
                      <p className="text-xs text-gray-400">
                        {format(parseISO(tx.created_at), 'M/d HH:mm', { locale: ja })}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Low Stock Items */}
      {data.low_stock_items.length > 0 && (
        <div className="card border-red-200 bg-red-50">
          <div className="card-header border-red-200">
            <h2 className="font-semibold text-red-800 flex items-center gap-2">
              <TrendingDown className="h-5 w-5" />
              在庫不足の商品
            </h2>
          </div>
          <div className="card-body">
            <ul className="divide-y divide-red-200">
              {data.low_stock_items.map((item) => (
                <li key={item.id} className="py-3 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{item.name}</p>
                    <p className="text-sm text-gray-500">
                      最小在庫: {item.min_stock}{item.unit}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-red-600">
                      {item.current_stock}{item.unit}
                    </p>
                    <p className="text-xs text-red-500">
                      不足: {item.min_stock - item.current_stock}{item.unit}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
